import { motion, AnimatePresence } from "framer-motion";
import { 
  BrainCircuit, 
  ScanLine, 
  Network, 
  Smartphone, 
  Languages,
  Timer,
  ShieldCheck,
  Heart,
  Hourglass,
  MessageCircle
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_digital_healthcare_intake_background.png";
const appScreen = "/assets/cards/mobile_ai_patient_intake_interface.png";

export default function AIIntakeCard() {
  const features = [
    {
      icon: BrainCircuit,
      title: "AI-Guided Symptom Capture",
      desc: "Conversational questionnaires that feel human, powered by clinical intelligence"
    },
    {
      icon: ScanLine,
      title: "Instant Document Intelligence",
      desc: "Upload an ID or insurance card; AI extracts and verifies in seconds"
    },
    {
      icon: Network,
      title: "Connected Health History",
      desc: "Pre-populated records from HIEs and past visits—no clipboard required"
    },
    {
      icon: Smartphone,
      title: "Reach Patients Anywhere",
      desc: "SMS check-in links, email forms, QR kiosks, and mobile-first experiences"
    },
    {
      icon: Languages,
      title: "Multilingual & Accessible",
      desc: "Real-time translation and ADA-compliant interfaces for every patient"
    }
  ];

  const kpis = [
    { label: "Intake Time", value: "-60%", icon: Timer, color: "text-emerald-400" },
    { label: "Data Accuracy", value: "98%", icon: ShieldCheck, color: "text-blue-400" },
    { label: "Patient Satisfaction", value: "+40%", icon: Heart, color: "text-pink-400" },
    { label: "Staff Time Saved", value: "15 min", icon: Hourglass, color: "text-amber-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            AI Intake
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            AI Enabled Intake
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            The First Touchpoint, Reimagined
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Smart intake that captures more, asks less, and gets patients to care faster."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/40 mix-blend-multiply" />
          </div>

          {/* Floating Phone with App Screen - Reduced height and moved up */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
            className="relative z-10 w-56 h-[420px] bg-slate-950 rounded-[2.5rem] border-8 border-slate-800 shadow-2xl overflow-hidden -mt-16"
          >
             {/* Phone Notch */}
             <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-800 rounded-b-xl z-30" />
             
             {/* Screen Content */}
             <img 
              src={appScreen} 
              alt="Mobile Intake App" 
              className="w-full h-full object-cover"
            />
             
             {/* Screen Glare */}
             <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none z-20" />
          </motion.div>

          {/* Floating Chat Bubbles - Left side (Patient responses) and Right side (AI questions) */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             {/* LEFT SIDE - Patient Responses - positioned very close to phone (4px gap) */}
             {/* Patient Response 1 - Top Left */}
             <motion.div
               animate={{ y: [0, -8, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
               className="absolute top-[15%] left-[28%] bg-blue-500/90 backdrop-blur text-white p-3 rounded-2xl rounded-bl-none shadow-lg max-w-[140px] text-xs font-medium"
             >
                <div className="flex items-center gap-1.5 mb-1.5 text-blue-100">
                 <MessageCircle className="w-3 h-3" /> Patient
               </div>
               "About 3 days, mostly in the morning."
             </motion.div>

             {/* Patient Response 2 - Middle Left */}
             <motion.div
               animate={{ y: [0, -10, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 4 }}
               className="absolute top-[40%] left-[27%] bg-blue-500/90 backdrop-blur text-white p-3 rounded-2xl rounded-bl-none shadow-lg max-w-[140px] text-xs font-medium"
             >
                <div className="flex items-center gap-1.5 mb-1.5 text-blue-100">
                 <MessageCircle className="w-3 h-3" /> Patient
               </div>
               "Just penicillin."
             </motion.div>

             {/* Patient Response 3 - Bottom Left */}
             <motion.div
               animate={{ y: [0, -6, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 5.5 }}
               className="absolute top-[62%] left-[28%] bg-blue-500/90 backdrop-blur text-white p-3 rounded-2xl rounded-bl-none shadow-lg max-w-[140px] text-xs font-medium"
             >
                <div className="flex items-center gap-1.5 mb-1.5 text-blue-100">
                 <MessageCircle className="w-3 h-3" /> Patient
               </div>
               "Lisinopril, 10mg daily."
             </motion.div>

             {/* RIGHT SIDE - AI Assistant Questions - positioned very close to phone (4px gap) */}
             {/* AI Question 1 - Top Right */}
             <motion.div
               animate={{ y: [0, -10, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
               className="absolute top-[10%] right-[27%] bg-white/90 backdrop-blur text-slate-800 p-3 rounded-2xl rounded-tr-none shadow-lg max-w-[140px] text-xs font-medium"
             >
               <div className="flex items-center gap-1.5 mb-1.5" style={{ color: PRIMARY_BLUE }}>
                 <BrainCircuit className="w-3 h-3" /> AI Assistant
               </div>
               "How long have the headaches lasted?"
             </motion.div>

             {/* AI Question 2 - Middle Right */}
             <motion.div
               animate={{ y: [0, -8, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2.5 }}
               className="absolute top-[35%] right-[28%] bg-white/90 backdrop-blur text-slate-800 p-3 rounded-2xl rounded-tr-none shadow-lg max-w-[140px] text-xs font-medium"
             >
               <div className="flex items-center gap-1.5 mb-1.5" style={{ color: PRIMARY_BLUE }}>
                 <BrainCircuit className="w-3 h-3" /> AI Assistant
               </div>
               "Any allergies we should know about?"
             </motion.div>

             {/* AI Question 3 - Bottom Right */}
             <motion.div
               animate={{ y: [0, -12, 0], opacity: [0.8, 1, 0.8] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 4.5 }}
               className="absolute top-[58%] right-[27%] bg-white/90 backdrop-blur text-slate-800 p-3 rounded-2xl rounded-tr-none shadow-lg max-w-[140px] text-xs font-medium"
             >
               <div className="flex items-center gap-1.5 mb-1.5" style={{ color: PRIMARY_BLUE }}>
                 <BrainCircuit className="w-3 h-3" /> AI Assistant
               </div>
               "Are you taking any medications?"
             </motion.div>

          </div>


          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

